import { useTranslation } from 'react-i18next';

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor,
} from '@/components/ui/combobox';
import { DynamicDateRangeType } from '@/types/dynamicDateRange';
import { getDynamicDateRangeLabel } from '@/utils/dynamicDateRange';

const DYNAMIC_DATE_OPTIONS: { label: string; value: DynamicDateRangeType }[] = [
  { label: 'Today', value: 'today' },
  { label: 'Tomorrow', value: 'tomorrow' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'This Week', value: 'thisWeek' },
  { label: 'Next Week', value: 'nextWeek' },
  { label: 'Last Week', value: 'lastWeek' },
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Next Month', value: 'nextMonth' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'Last 7 Days', value: 'last7Days' },
  { label: 'Last 30 Days', value: 'last30Days' },
];

type DynamicDateRangeSelectProps = {
  container?: React.ComponentProps<typeof ComboboxContent>['container'];
  value: DynamicDateRangeType[];
  onChange: (value: DynamicDateRangeType[]) => void;
};

export const DynamicDateRangeSelect = ({
  container,
  value,
  onChange,
}: DynamicDateRangeSelectProps) => {
  const { t } = useTranslation();
  const anchorRef = useComboboxAnchor();

  return (
    <div className="mb-3">
      <label className="mb-2 block text-sm font-medium">
        Dynamic Range Type
      </label>
      <Combobox
        multiple
        value={value}
        onValueChange={(val) => onChange(val as DynamicDateRangeType[])}
      >
        <ComboboxChips ref={anchorRef} className="w-full">
          {value.map((type) => (
            <ComboboxChip key={type} {...({ value: type } as any)}>
              {getDynamicDateRangeLabel(type)}
            </ComboboxChip>
          ))}
          <ComboboxChipsInput
            placeholder={value.length === 0 ? 'Select range types' : ''}
          />
        </ComboboxChips>
        <ComboboxContent anchor={anchorRef} container={container}>
          <ComboboxList>
            {DYNAMIC_DATE_OPTIONS.map((opt) => (
              <ComboboxItem key={opt.value} value={opt.value}>
                {opt.label}
              </ComboboxItem>
            ))}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      <p className="mt-1 text-xs text-gray-500">
        {t('customViews.dynamicDateRangeHint')}
      </p>
    </div>
  );
};
